import React, { useEffect, useRef, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { apiFetch, API_BASE } from "./utils/api";
import { escapeHtml, columnLabel, answerCellHtml } from "./utils/resultsHtml";
import { decodeFormUrl } from "./utils/urlEncoder";
import $ from "jquery";
import "datatables.net";
import "datatables.net-dt/css/dataTables.dataTables.min.css";
import * as XLSX from "sheetjs-style";
import {
    FaEnvelope, FaHashtag, FaList, FaCheckSquare, FaCaretDown,
    FaCalendarAlt, FaAlignLeft, FaFileAlt, FaToggleOn, FaImage, FaBoxes, FaGripHorizontal,
    FaHeading, FaChevronUp, FaChevronDown, FaClock, FaRegClock, FaCalendarCheck, FaSortNumericDown, FaStar,
    FaSlidersH, FaSmile, FaEquals, FaBars, FaMapMarkerAlt, FaVideo, FaFilePdf, FaMinus, FaYoutube, FaSignature
} from "react-icons/fa";
import { exportResponsesExcel, exportResponsesPdf } from "./utils/dForms_Export";
import { formatIndiaDateTime, formatCalendarDate, indiaDateKey, quickDateRange } from "./utils/indiaTime";
import InsightsView from "./InsightsView";
import "./Responses.css";
import PendingView from "./PendingView";

const fieldIcons = {
    "Heading": <FaHeading />,
    "Paragraph": <FaAlignLeft />,
    "Banner": <FaFileAlt />,

    "Dropdown": <FaCaretDown />,
    "Picture": <FaImage />,
    "Multiple Select": (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', color: "#F59E0B" }}>
            <FaChevronUp style={{ height: '0.45rem', width: '0.65rem' }} />
            <FaChevronDown style={{ height: '0.45rem', width: '0.65rem' }} />
        </div>
    ),
    "Switch": <FaToggleOn />,
    "Multiple Choice": <FaList />,
    "Multiple Select Checkboxes": <FaBoxes />,
    "Choice Matrix": <FaGripHorizontal />,
    "Checkbox": <FaCheckSquare />,

    "Date Picker": <FaCalendarAlt />,
    "Date Time Picker": <FaClock />,
    "Time Picker": <FaRegClock />,
    "Date Range": <FaCalendarCheck />,

    "Ranking": <FaSortNumericDown />,
    "Star Rating": <FaStar />,
    "Slider": <FaSlidersH />,
    "Opinion Scale": <FaSmile />,

    "Short Answer": <FaEquals />,
    "Long Answer": <FaBars />,

    "Email": <FaEnvelope />,
    "Number": <FaHashtag />,
    "Address": <FaMapMarkerAlt />,
    "Document Type": <FaFileAlt />,
    "Signature": <FaSignature />,

    "Image": <FaImage />,
    "Video": <FaVideo />,
    "PDF": <FaFilePdf />,
    "YouTubeVideo": <FaYoutube style={{ color: "#FF0000" }} />,

    "Divider": <FaMinus />
};

// ---- Submitted at / by, as the owner may see them --------------------------
// Anonymous forms: the API sends the date only ("YYYY-MM-DD") and "Anonymous"
// as the submitter; every view and export shows exactly that.

// India time for everyone (date only on anonymous forms).
const formatSubmitted = (row) => {
    if (!row || !row.submitted_at) return "";
    return row.anonymous ? formatCalendarDate(row.submitted_at) : formatIndiaDateTime(row.submitted_at);
};

const submittedHeader = (rows) => (rows[0] && rows[0].anonymous ? "Submitted on" : "Submitted At");

// ---- Questions (columns) ----------------------------------------------------
// The server groups answers by question across form edits (question_key) and
// says which page each is on. Answers without a key (never expected) fall
// back to their own field.
const questionKey = (answer) => answer.question_key || `f${answer.field_id}`;

// Every question answered in these responses, in page order then question order.
const questionList = (rows) => {
    const byKey = new Map();
    rows.forEach(r => r.answers.forEach(a => {
        const key = questionKey(a);
        if (!byKey.has(key)) {
            byKey.set(key, {
                key,
                label: a.label,
                type: a.type,
                page_id: a.page_id !== undefined ? String(a.page_id) : "",
                page_title: a.page_title || "",
                order: a.question_order !== undefined ? a.question_order : 1e6 + Number(a.field_id),
            });
        }
    }));
    return [...byKey.values()].sort((a, b) => a.order - b.order);
};

// Pages that have answered questions, in order: [{ page_id, page_title }].
const pageList = (questions) => {
    const seen = new Map();
    questions.forEach(q => { if (!seen.has(q.page_id)) seen.set(q.page_id, { page_id: q.page_id, page_title: q.page_title || "Page" }); });
    return [...seen.values()];
};

const findAnswer = (row, key) => row.answers.find(a => questionKey(a) === key);

// Question types whose answers are long: wider, wrapped export columns.
const WIDE_TYPES = ["Long Answer", "Paragraph", "Address", "Choice Matrix", "Multiple Select Checkboxes", "Signature", "Document Type"];

// PDF: a question column at least as wide as the longest word of its
// heading (bold 10.5pt, about 6.3pt a letter, plus padding), so no word is
// split; emails and numbers get room for a whole value.
const pdfMinWidth = (meta) => {
    const longestWord = Math.max(0, ...String(meta.label || "").split(/\s+/).map(w => w.length));
    const base = meta.type === "Email" ? 170 : meta.type === "Number" ? 80 : 60;
    return Math.min(Math.max(base, Math.round(longestWord * 6.3 + 20)), 170);
};

// An answer as plain text (detail view).
const answerText = (answer) => {
    if (!answer || answer.answer === null || answer.answer === undefined || answer.answer === "") return "";
    if (answer.document) return answer.answer;
    try {
        const parsed = JSON.parse(answer.answer);
        if (Array.isArray(parsed)) return parsed.join(", ");
        if (parsed && typeof parsed === "object") {
            return Object.entries(parsed).map(([k, v]) => `${k}: ${typeof v === "object" ? JSON.stringify(v) : v}`).join("; ");
        }
        return String(parsed);
    } catch (e) {
        return String(answer.answer);
    }
};

const Responses = () => {
    const navigate = useNavigate();

    const { token } = useParams();

    const decoded = useMemo(() => {
        return token ? decodeFormUrl(token) : null;
    }, [token]);

    const formId = decoded?.formId || null;

    const [responses, setResponses] = useState([]);
    const [columns, setColumns] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const tableRef = useRef(null);
    const dataTableRef = useRef(null);
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const dropdownRef = useRef(null);
    const [summaryData, setSummaryData] = useState([]);
    const [viewMode, setViewMode] = useState("results"); // 'results' | 'summary' | 'pending'
    const [detail, setDetail] = useState(null); // the response open in the detail view
    // Questions across all pages, and the page shown ("all" or a page_id).
    const [questions, setQuestions] = useState([]);
    const [pageFilter, setPageFilter] = useState("all");
    const pageFilterRef = useRef("all");
    pageFilterRef.current = pageFilter;
    const pages = useMemo(() => pageList(questions), [questions]);
    const shownQuestions = useMemo(
        () => (pageFilter === "all" ? questions : questions.filter(q => q.page_id === pageFilter)),
        [questions, pageFilter]
    );
    const pageFilterTitle = pageFilter === "all" ? "" : ((pages.find(p => p.page_id === pageFilter) || {}).page_title || "");

    const generateSummaryData = (responses) => {
        const questionSummaryMap = {};

        responses.forEach(response => {
            response.answers.forEach(answer => {
                const key = questionKey(answer);
                const label = answer.label;
                const isAnswered = answer.answer !== null && answer.answer !== "";

                if (!questionSummaryMap[key]) {
                    questionSummaryMap[key] = { count: 0, total: 0, label: label, type: answer.type };
                }

                if (isAnswered) {
                    questionSummaryMap[key].count += 1;
                }

                questionSummaryMap[key].total += 1;
            });
        });

        // In question order (page order, then order on the page).
        const order = questionList(responses).map(q => q.key);
        return Object.entries(questionSummaryMap)
            .sort(([a], [b]) => order.indexOf(a) - order.indexOf(b))
            .map(([, item]) => item);
    };

    //fetch functions (response, profile)
    useEffect(() => {
        fetchUserProfile();

        apiFetch(`/api/responses/get-responses/${formId}`, {
            method: "GET",
            credentials: "include",
        })
            .then((res) => {
                if (!res.ok) {
                    throw new Error("Failed to fetch responses");
                }
                return res.json();
            })
            .then((data) => {
                setResponses(data);

                // ✅ Generate Summary
                const summary = generateSummaryData(data);
                setSummaryData(summary);

                if (data.length === 0) {
                    setColumns([]);
                    return;
                }

                // One column per question (grouped by the server across form
                // edits), in page order then question order.
                const allQuestions = questionList(data);
                setQuestions(allQuestions);

                const dynamicColumns = [
                    { title: "Response ID", data: "response_id" },
                    {
                        title: submittedHeader(data),
                        data: "submitted_at",
                        render: (value, type, row) => {
                            if (type !== "display") return value;
                            const edited = row.edited_at
                                ? `<small class="edited-note">Edited ${escapeHtml(formatIndiaDateTime(row.edited_at))}</small>`
                                : "";
                            return `${escapeHtml(formatSubmitted(row))}${edited}`;
                        },
                    },
                    {
                        title: "Submitted by",
                        data: "submitted_by_name",
                        render: (value) => escapeHtml(value || ""),
                    },
                    ...allQuestions.map((question) => ({
                        // DataTables puts the title in as HTML; the question
                        // text itself is kept in label.
                        title: escapeHtml(question.label),
                        label: question.label,
                        pageId: question.page_id,
                        data: (row) => answerCellHtml(findAnswer(row, question.key), API_BASE),
                    }))
                ];

                setColumns(dynamicColumns);
            })
            .catch((err) => setError(err.message))
            .finally(() => setLoading(false));

    }, [formId]);

    // Fetch user profile to ensure authentication
    const fetchUserProfile = async () => {
        try {
            const res = await apiFetch("/api/leftnavbar/get-user-profile", {
                method: "GET",
                credentials: "include",
            });

            if (!res.ok) {
                throw new Error("Unauthorized");
            }

            const profileData = await res.json();

            if (!profileData?.emp_id) {
                throw new Error("No user found");
            }

            return profileData;

        } catch (error) {
            navigate("/login");
        }
    };

    // Show/hide clear icon when date changes
    const toggleClearIcon = () => {
        if ($('#minDate').val() || $('#maxDate').val()) {
            $('#clearDateFilter').show();
        } else {
            $('#clearDateFilter').hide();
        }
    };

    // "Page" picker: show only that page's question columns (Response ID,
    // Submitted and Submitted by always show).
    const applyPageFilter = (value) => {
        const table = dataTableRef.current;
        if (!table) return;
        columns.forEach((col, i) => {
            if (i < 3) return;
            table.column(i).visible(value === "all" || col.pageId === value, false);
        });
        table.columns.adjust().draw(false);
    };
    useEffect(() => {
        applyPageFilter(pageFilter);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pageFilter]);

    // Initialize DataTable when responses or columns change
    useEffect(() => {
        if (responses.length > 0 && columns.length > 0 && tableRef.current) {
            if (dataTableRef.current) {
                dataTableRef.current.destroy();
                dataTableRef.current = null;
            }

            $.fn.dataTable.ext.search.length = 0;

            dataTableRef.current = $(tableRef.current).DataTable({
                data: responses,
                columns: columns,
                destroy: true,
                responsive: true,
                searching: true,
                paging: true,
                ordering: true,
                info: true,
                dom: '<"top-bar-wrapper"lf<"date-filter-wrapper"><"column-toggle-wrapper"><"custom-filter-wrapper">>rt<"bottom"ip><"clear">'
            });

            applyPageFilter(pageFilterRef.current);

            // Click a row (not a file link in it) to open the detail view.
            $(tableRef.current).off("click", "tbody tr").on("click", "tbody tr", function (e) {
                if ($(e.target).closest("a").length) return;
                const row = dataTableRef.current && dataTableRef.current.row(this).data();
                if (row) setDetail(row);
            });

            $('.column-toggle-wrapper').html(`
            <button id="toggleColumnDropdown" style="
                background:none;
                border:1px solid #ccc;
                border-radius:4px;
                cursor:pointer;
                padding:6px 12px;
                font-size:14px;
                display:flex;
                align-items:center;
                gap:4px;
            ">
                <i className="fa-solid fa-eye-slash" style="color: #2D3748;"></i> Hide Columns
            </button>
            `);

            const generateDropdownHTML = () => {
                return `
                        <div id="columnToggleList">
                            ${columns.map((col, i) => `
                                <div style="display:flex; align-items:center; gap:8px; margin-bottom:10px;">
                                    <input 
                                        id="col-toggle-${i}"
                                        type="checkbox" 
                                        class="toggle-col" 
                                        data-column-index="${i}" 
                                        ${i < 2 ? 'checked disabled' : 'checked'}
                                    />
                                    <label 
                                        for="col-toggle-${i}" 
                                        style="font-size:14px; cursor:pointer;"
                                    >
                                        ${escapeHtml(columnLabel(col))}
                                    </label>
                                </div>
                            `).join('')}
                        </div>
                        <div style="text-align:right; margin-top:10px;">
                            <button id="toggleAllColumns"
                                style="
                                    background:#f9f9f9;
                                    border:1px solid #ccc;
                                    padding:6px 12px;
                                    border-radius:4px;
                                    font-size:13px;
                                    cursor:pointer;
                                    border: 2px solid #3B82F6;
                                ">Hide all</button>
                        </div>
                        `;
            };

            let allVisible = true;

            // Open dropdown
            $(document).off('click', '#toggleColumnDropdown')
                .on('click', '#toggleColumnDropdown', function (e) {
                    const $menu = $('#globalColumnDropdownMenu');
                    const rect = e.currentTarget.getBoundingClientRect();

                    if ($menu.is(':visible')) {
                        $menu.hide();
                        return;
                    }

                    $menu.html(generateDropdownHTML());
                    $menu.css({
                        display: 'block',
                        top: rect.bottom + window.scrollY + 'px',
                        left: rect.left + window.scrollX + 'px'
                    });
                });

            // Individual column toggle
            $(document).off('change', '.toggle-col')
                .on('change', '.toggle-col', function () {

                    const index = parseInt($(this).attr('data-column-index'), 10);

                    if ($(this).is(':disabled')) return;

                    const isChecked = $(this).is(':checked');

                    dataTableRef.current
                        .column(index)
                        .visible(isChecked);

                });

            // Hide / Show all
            $(document).off('click', '#toggleAllColumns')
                .on('click', '#toggleAllColumns', function () {
                    allVisible = !allVisible;

                    dataTableRef.current.columns().every(function (idx) {
                        this.visible(allVisible, false);
                        $(`.toggle-col[data-column-index="${idx}"]`).prop('checked', allVisible);
                    });

                    dataTableRef.current.columns.adjust().draw(false);
                    $(this).text(allVisible ? 'Hide all' : 'Show all');
                });

            $(document).off('click.hideDropdown')
                .on('click.hideDropdown', function (e) {
                    if (!$(e.target).closest('#globalColumnDropdownMenu, #toggleColumnDropdown').length) {
                        $('#globalColumnDropdownMenu').hide();
                    }
                });

            $('.custom-filter-wrapper').html(`
                <div style="position:relative; display:inline-block; margin-left:1px;">
                    <button id="toggleCustomFilter" style="
                        background:none;
                        border:1px solid #ccc;
                        border-radius:4px;
                        cursor:pointer;
                        padding:6px 12px;
                        font-size: 14px;
                        display:flex;
                        align-items:center;
                        gap:4px;
                    ">
                    <i className="fa-solid fa-filter" style="color: #2D3748;"></i> Filter
                    </button>

                    <div id="customFilterPanel" style="
                        display:none;
                        position:absolute;
                        top:110%;
                        left:0;
                        background:#fff;
                        border:1px solid #ddd;
                        border-radius: 6px;
                        padding: 12px;
                        box-shadow: 0 4px 12px rgba(0,0,0,0.15);
                        z-index:999;
                        width:420px;
                    ">
                       <div style="display: grid; grid-template-columns: 1fr 1fr 2fr auto; gap: 8px; align-items: center;">
                            <select id="filterField" style="padding: 6px 8px; width: 100%;">
                                ${columns.map(col => `<option value="${escapeHtml(columnLabel(col))}">${escapeHtml(columnLabel(col))}</option>`).join('')}
                            </select>
                            <select id="filterOperator" style="padding: 6px 8px; width: 100%;">
                                <option value="equals">equals</option>
                                <option value="contains">contains</option>
                            </select>
                            <input type="text" id="filterValue" style="padding: 6px 8px; width: 100%;" placeholder="Value" />
                            <button id="applyCustomFilter" style="
                                background: #000;
                                color: #fff;
                                padding: 6px 12px;
                                border: none;
                                border-radius: 4px;
                                cursor: pointer;
                                white-space: nowrap;
                            ">Apply</button>
                        </div>
                    </div>
                </div>
            `);

            $(document).off('click', '#toggleCustomFilter')
                .on('click', '#toggleCustomFilter', function () {
                    $('#customFilterPanel').toggle();
                });

            $('.date-filter-wrapper').html(`
            <div style="position:relative; display:inline-block;">
                <button id="toggleDateFilter" style="
                background:none;
                border:1px solid #ccc;
                border-radius:4px;
                cursor:pointer;
                padding:6px 12px;
                font-size: 14px;
                display:flex;
                align-items:center;
                gap:4px;
                ">
                <i className="fa-solid fa-calendar-days" style="color: #2D3748;"></i> Date Range
                </button>
                <div id="dateRangeInputs" style="
                display:none;
                position:absolute;
                left:0;
                top:110%;
                background:#fff;
                border:1px solid #ddd;
                border-radius: 6px;
                padding:12px;
                box-shadow: 0 4px 12px rgba(0,0,0,0.15);
                z-index:999;
                min-width: 280px;
                ">
                <div style="display:flex; align-items:center; margin-bottom:12px; gap:10px;">
                    <div style="display:flex; gap:8px; flex:1;">
                        <input type="date" id="minDate" placeholder="From" style="flex:1; padding:6px 10px; border:1px solid #ccc; border-radius:4px;" />
                        <span style="color:#555;">→</span>
                        <input type="date" id="maxDate" placeholder="To" style="flex:1; padding:6px 10px; border:1px solid #ccc; border-radius:4px;" />
                    </div>
                    <!-- CLOSE ICON RIGHT AFTER INPUTS -->
                    <i id="clearDateFilter" className="fa-solid fa-xmark" style="
                        display:none;
                        padding:6px;
                        border-radius:50%;
                        background:#eee;
                        color:#555;
                        font-size:13px;
                        cursor:pointer;
                        border:1px solid #ccc;
                        transition: background 0.2s;
                    " title="Clear date range"></i>
                    </div>

                <div style="display:flex; gap:6px; flex-wrap:wrap;">
                    <button type="button" className="quick-date" data-days="0" style="font-size:12px; padding:4px 8px;">Today</button>
                    <button type="button" className="quick-date" data-days="7" style="font-size:12px; padding:4px 8px;">Last 7 days</button>
                    <button type="button" className="quick-date" data-days="28" style="font-size:12px; padding:4px 8px;">Last 4 weeks</button>
                    <button type="button" className="quick-date" data-days="365" style="font-size:12px; padding:4px 8px;">Last 12 months</button>
                </div>
                </div>
            </div>
            `);

            $(document).off('click', '#toggleDateFilter')
                .on('click', '#toggleDateFilter', function () {
                    $('#dateRangeInputs').toggle();
                });

            let customFilterActive = false;
            let customFilterParams = {};

            $.fn.dataTable.ext.search.push(function (settings, data, dataIndex) {

                const minDate = $('#minDate').val();
                const maxDate = $('#maxDate').val();

                if (!minDate && !maxDate) return true;

                const rowData = dataTableRef.current.row(dataIndex).data();
                if (!rowData || !rowData.submitted_at) return true;

                // India calendar dates, compared as "YYYY-MM-DD" strings, so the
                // filter means the same day whatever the device's time zone.
                const rowDay = indiaDateKey(rowData.submitted_at);
                return (!minDate || rowDay >= minDate) &&
                    (!maxDate || rowDay <= maxDate);
            });

            $(document).off('change', '#minDate, #maxDate')
                .on('change', '#minDate, #maxDate', function () {
                    toggleClearIcon();
                    dataTableRef.current.draw();
                });

            // Always unbind before binding to avoid duplicate handlers
            $(document).off('click', '#applyCustomFilter').on('click', '#applyCustomFilter', function () {
                const field = $('#filterField').val();
                const operator = $('#filterOperator').val();
                const value = $('#filterValue').val().trim();

                if (!value) {
                    customFilterActive = false;
                    customFilterParams = {};
                } else {
                    customFilterActive = true;
                    customFilterParams = { field, operator, value };
                }

                dataTableRef.current.draw();
            });

            $(document).off('click', '#clearDateFilter')
                .on('click', '#clearDateFilter', function () {
                    $('#minDate').val('');
                    $('#maxDate').val('');
                    dataTableRef.current.draw();
                    toggleClearIcon();
                });

            $(document).off('click', '.quick-date')
                .on('click', '.quick-date', function () {
                    const days = parseInt($(this).data('days'), 10);
                    // India calendar dates (toISOString would give the UTC date).
                    const range = quickDateRange(days);
                    $('#minDate').val(range.from);
                    $('#maxDate').val(range.to);

                    dataTableRef.current.draw();
                    toggleClearIcon();
                });

            $(document).off('click', '#minDate, #maxDate')
                .on('click', '#minDate, #maxDate', function (e) {
                    this.showPicker?.();
                });
        }

        return () => {
            if (dataTableRef.current) {
                dataTableRef.current.destroy();
                dataTableRef.current = null;
            }
            $.fn.dataTable.ext.search = [];
            $(document).off('click', '#toggleColumnDropdown');
            $(document).off('click', '#toggleAllColumns');
            $(document).off('click', '.toggle-col');
        };
    }, [responses, columns]);

    // Close dropdowns when clicking outside
    useEffect(() => {
        const handleOutsideClick = (e) => {
            const $datePopup = $('#dateRangeInputs');
            const $dateButton = $('#toggleDateFilter');
            const $columnPopup = $('#globalColumnDropdownMenu');
            const $columnButton = $('#toggleColumnDropdown');
            const $customFilter = $('#customFilterPanel');
            const $customFilterButton = $('#toggleCustomFilter');

            // Close date popup
            if (!$datePopup.is(e.target) && $datePopup.has(e.target).length === 0 &&
                !$dateButton.is(e.target) && $dateButton.has(e.target).length === 0) {
                $datePopup.hide();
            }

            // Close column popup
            if (!$columnPopup.is(e.target) && $columnPopup.has(e.target).length === 0 &&
                !$columnButton.is(e.target) && $columnButton.has(e.target).length === 0) {
                $columnPopup.hide();
            }

            // Close custom filter panel
            if (!$customFilter.is(e.target) && $customFilter.has(e.target).length === 0 &&
                !$customFilterButton.is(e.target) && $customFilterButton.has(e.target).length === 0) {
                $customFilter.hide();
            }

            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
                setDropdownOpen(false);
            }
        };

        document.addEventListener('mousedown', handleOutsideClick);
        return () => {
            document.removeEventListener('mousedown', handleOutsideClick);
        };
    }, []);

    // ---- Exports (Excel, PDF) in the Dolluz Corp theme, see utils/dForms_Export.js
    // Columns as on the Results table: Response ID, Submitted At, Submitted
    // by, then one per question (by field id, in first-seen order).
    // The page picked in "Page" limits the questions exported.
    const exportData = () => {
        const anyEdited = responses.some(r => r.edited_at);
        const fixed = [
            { key: "response_id", label: "Response ID", width: 13, pdfWidth: 80 },
            { key: "submitted_at", label: submittedHeader(responses), width: 24, pdfWidth: 140 },
            { key: "submitted_by", label: "Submitted by", width: 24, pdfWidth: 95 },
            ...(anyEdited ? [{ key: "edited_at", label: "Last edited", width: 24, pdfWidth: 140 }] : []),
        ];
        const columns = [
            ...fixed,
            ...shownQuestions.map((q) => ({
                key: `k_${q.key}`,
                label: q.label || q.type || "Question",
                width: WIDE_TYPES.includes(q.type) ? 40 : q.type === "Email" ? 30 : 24,
                wrap: WIDE_TYPES.includes(q.type),
                pdfWidth: pdfMinWidth(q),
                questionKey: q.key,
            })),
        ];
        const rows = responses.map(res => {
            const row = {
                response_id: res.response_id,
                submitted_at: formatSubmitted(res),
                submitted_by: res.submitted_by_name || "",
                edited_at: res.edited_at ? formatIndiaDateTime(res.edited_at) : "",
            };
            columns.slice(fixed.length).forEach(c => {
                const match = findAnswer(res, c.questionKey);
                row[c.key] = match ? formatAnswerForPDF(match.answer) : "";
            });
            return row;
        });
        const formTitle = responses[0]?.formTitle || "Untitled";
        return {
            moduleName: "dForms",
            sheetTitle: `Responses for "${formTitle}"`,
            filterLabel: `Form ID: ${formId}${pageFilterTitle ? ` | Page: ${pageFilterTitle}` : ""}`,
            columns,
            fixedKeys: fixed.map(c => c.key),
            rows,
        };
    };

    const exportToExcel = async () => {
        if (responses.length === 0) {
            alert("No data available to export.");
            return;
        }
        const { fixedKeys, ...data } = exportData();
        await exportResponsesExcel({ ...data, filename: `Responses_${formId}.xlsx` });
        logExport("excel", responses.length);
    };

    // Record the export in the form's activity log. Fire-and-forget: a failed
    // log never blocks or breaks the export itself.
    const logExport = (format, count) => {
        apiFetch(`/api/responses/${formId}/export-log`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ format, count, filters: null }),
        }).catch(() => { });
    };

    const formatAnswerForPDF = (answer) => {
        if (answer == null) return '';

        try {
            const parsed = JSON.parse(answer);

            if (Array.isArray(parsed)) {
                return parsed.join(", ");
            } else if (typeof parsed === "object" && parsed !== null) {
                return Object.entries(parsed)
                    .map(([k, v]) => {
                        if (typeof v === 'object') {
                            return `${k}: ${JSON.stringify(v)}`;
                        }
                        return `${k}: ${v}`;
                    })
                    .join("\n");
            } else {
                return String(parsed); // for booleans, numbers
            }
        } catch (e) {
            // fallback: raw text
            return String(answer).trim();
        }
    };

    const exportToPDF = async () => {
        if (responses.length === 0) {
            alert("No data available to export.");
            return;
        }

        // Signature images, fetched with the owner's sign-in and drawn into
        // their cells (under the "Signed ... at ..." text).
        const signatureImages = {};
        await Promise.all(responses.flatMap(r => r.answers.filter(a => a.signature)).map(async (a) => {
            try {
                const res = await fetch(`${API_BASE}${a.signature.url}`, { credentials: "include" });
                if (!res.ok) return;
                const blob = await res.blob();
                signatureImages[a.signature.url] = await new Promise((resolve) => {
                    const reader = new FileReader();
                    reader.onload = () => resolve(reader.result);
                    reader.onerror = () => resolve(null);
                    reader.readAsDataURL(blob);
                });
            } catch (e) {
                // the cell keeps its text
            }
        }));

        const { fixedKeys, ...data } = exportData();
        const questionOf = Object.fromEntries(data.columns.filter(c => c.questionKey).map(c => [c.key, c.questionKey]));
        exportResponsesPdf({
            ...data,
            filename: `Responses_${formId}.pdf`,
            repeatKeys: fixedKeys,
            cellImage: (rowIndex, key) => {
                if (!questionOf[key]) return null;
                const answer = responses[rowIndex] && findAnswer(responses[rowIndex], questionOf[key]);
                return answer && answer.signature ? signatureImages[answer.signature.url] || null : null;
            },
        });
        logExport("pdf", responses.length);
    };

    if (loading) return <p>Loading responses...</p>;
    if (error) return <p>Error: {error}</p>;

    return (
        <div className="responses-container">
            <div className="responses-header-tabs">
                <div
                    className={`tab ${viewMode === "results" ? "active-tab" : ""}`}
                    onClick={() => setViewMode("results")}
                >
                    <i className="fa-solid fa-table-cells"></i> Results
                </div>
                <div
                    className={`tab ${viewMode === "summary" ? "active-tab" : ""}`}
                    onClick={() => setViewMode("summary")}
                >
                    <i className="fa-solid fa-chart-pie"></i> Summary
                </div>
                <div
                    className={`tab ${viewMode === "pending" ? "active-tab" : ""}`}
                    onClick={() => setViewMode("pending")}
                >
                    <i className="fa-solid fa-user-clock"></i> Pending
                </div>
                <div
                    className={`tab ${viewMode === "insights" ? "active-tab" : ""}`}
                    onClick={() => setViewMode("insights")}
                >
                    <i className="fa-solid fa-chart-line"></i> Insights
                </div>
            </div>

            {viewMode === "pending" && <PendingView formId={formId} />}
            {viewMode === "insights" && <InsightsView formId={formId} />}

            <div style={{ display: viewMode === "pending" || viewMode === "insights" ? "none" : "block", backgroundColor: viewMode === "summary" ? "rgb(249 250 251)" : "transparent" }}>
                {responses.length > 0 && (
                    <div className="export-dropdown" ref={dropdownRef}>
                        <button className="export-btn" onClick={() => setDropdownOpen(!dropdownOpen)}>
                            Export <i className="fa-solid fa-caret-down" style={{ marginLeft: "2px", fontSize: "1.1rem" }}></i>
                        </button>
                        {dropdownOpen && (
                            <div className="dropdown-menu">
                                <button type="button" onClick={exportToExcel}>Excel</button>
                                <button type="button" onClick={exportToPDF}>PDF</button>
                            </div>
                        )}
                    </div>
                )}
                {responses.length > 0 && pages.length > 1 && viewMode === "results" && (
                    <label className="results-page-filter">
                        <span>Page</span>
                        <select value={pageFilter} onChange={(e) => setPageFilter(e.target.value)} aria-label="Show the answers of one page">
                            <option value="all">All pages</option>
                            {pages.map(p => <option key={p.page_id} value={p.page_id}>{p.page_title}</option>)}
                        </select>
                    </label>
                )}

                <div style={{ display: viewMode === "results" ? "block" : "none" }}>
                    {responses.length === 0 ? (
                        <p>No responses available.</p>
                    ) : (
                        <>
                            <div className="data-table-scroll-wrapper" style={{ marginTop: "20px" }}>
                                <table ref={tableRef} className="display">
                                    <thead>
                                        <tr>
                                            {columns.map((col, index) => (
                                                <th key={index} style={{ whiteSpace: "nowrap" }}>{columnLabel(col)}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody></tbody>
                                </table>
                            </div>
                            <div id="globalColumnDropdownMenu"
                                style={{
                                    display: 'none',
                                    position: 'absolute',
                                    zIndex: 9999,
                                    background: '#fff',
                                    border: '1px solid #ddd',
                                    borderRadius: '6px',
                                    padding: '12px',
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                    minWidth: '260px',
                                    maxHeight: '400px',
                                    overflowY: 'auto'
                                }}
                            ></div>
                        </>
                    )}
                </div>

                <div style={{ display: viewMode === "summary" ? "block" : "none" }}>
                    {summaryData.length === 0 ? (
                        <p>No responses available.</p>
                    ) : (
                        <div className="summary-wrapper">
                            <div className="summary-section">
                                {summaryData.map((item, index) => (
                                    <div key={index} className="summary-group-card">
                                        <div className="summary-question">
                                            <div className="summary-icon">
                                                {fieldIcons[item.type] || <FaAlignLeft />}
                                            </div>
                                            <div className="summary-question-text">
                                                {item.label}
                                            </div>
                                            <div className="summary-answered">
                                                {item.count} of {item.total} answered
                                            </div>
                                        </div>
                                        <div className="summary-footer-text">
                                            {item.count === 0 ? "No responses yet" : `${item.count} responses received`}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

            </div>

            {detail && (
                <ResponseDetail
                    response={detail}
                    questions={questions}
                    onClose={() => setDetail(null)}
                />
            )}
        </div>
    );
};

// One response in full: every answer, plus submitted at / by (date only and
// "Anonymous" on anonymous forms, like the table and exports).
const ResponseDetail = ({ response, questions, onClose }) => {
    useEffect(() => {
        const onKey = (e) => { if (e.key === "Escape") onClose(); };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [onClose]);

    // In question order, under a heading per page (when there are several).
    const order = questions.map(q => q.key);
    const answers = [...response.answers].sort((a, b) => order.indexOf(questionKey(a)) - order.indexOf(questionKey(b)));
    const multiPage = new Set(questions.map(q => q.page_id)).size > 1;
    const withHeadings = [];
    answers.forEach((a, i) => {
        const page = String(a.page_id ?? "");
        if (multiPage && (i === 0 || String(answers[i - 1].page_id ?? "") !== page)) {
            withHeadings.push({ heading: a.page_title || "Page", key: `page-${page}-${i}` });
        }
        withHeadings.push({ answer: a, key: `a-${a.field_id}` });
    });

    return (
        <div className="response-detail-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
            <div className="response-detail" role="dialog" aria-modal="true" aria-labelledby="response-detail-title">
                <div className="response-detail-head">
                    <h5 id="response-detail-title">Response #{response.response_id}</h5>
                    <button type="button" className="response-detail-close" aria-label="Close" onClick={onClose}>
                        <i className="fa-solid fa-xmark"></i>
                    </button>
                </div>
                <dl className="response-detail-meta">
                    <dt>{response.anonymous ? "Submitted on" : "Submitted at"}</dt>
                    <dd>{formatSubmitted(response)}</dd>
                    <dt>Submitted by</dt>
                    <dd>{response.submitted_by_name || ""}</dd>
                    {response.edited_at && (
                        <>
                            <dt>Last edited</dt>
                            <dd>{formatIndiaDateTime(response.edited_at)}</dd>
                        </>
                    )}
                </dl>
                <div className="response-detail-answers">
                    {answers.length === 0 && <p className="response-detail-empty">No answers.</p>}
                    {withHeadings.map(({ heading, answer: a, key }) => heading ? (
                        <h6 className="response-detail-page" key={key}>{heading}</h6>
                    ) : (
                        <div className="response-detail-answer" key={key}>
                            <div className="response-detail-question">{a.label}</div>
                            <div className="response-detail-value">
                                {a.signature
                                    ? (
                                        <figure className="response-detail-signature">
                                            <img src={`${API_BASE}${a.signature.url}`} alt={a.answer} />
                                            <figcaption>{a.answer}</figcaption>
                                        </figure>
                                    )
                                    : a.document
                                        ? <a href={`${API_BASE}${a.document.url}`} target="_blank" rel="noreferrer">{a.answer}</a>
                                        : (answerText(a) || "-")}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default Responses;
